/** Copyright (c) 2017 T.Rowe Price. All rights reserved. */

import './Footer.scss';

import moment from 'moment';
import classNames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import headingImage from 'assets/footer-tagline.png';
import socialIcons from 'assets/footer-icons.png';

import FooterDisclosures from 'components/layouts/Footer/FooterDisclosures';
import Optioner from 'components/shared/Optioner/Optioner';
import { footerLinks } from 'services/Externals';
import Factset from 'components/shared/Factset/Factset';
import TRP from 'components/shared/TRP/TRP';
import {
  isMobile,
  onAccountsPage,
  getView
} from 'state/view/ViewSelectors';
import { isDashboardOrAccounts } from 'state/router/RouterSelectors';
import { getHasReceivedAccounts } from 'state/requests/RequestStatusSelectors';
import { FooterLoadingSkeleton } from 'components/layouts/LoadingSkeleton/LoadingSkeleton';
import { intradayRefreshEnabled,
    owned529OnlyBrandingEnabled
} from 'services/FeatureFlags';
import {
  getHasOwnedCollegeSavingsOnly,
  hasOwnedAlaska529,
  hasOwnedAlaska529Only,
  hasOwnedBrokerage,
  hasOwnedMaryland529,
  hasOwnedMaryland529Only,
  hasOwnedNational529,
  hasOwnedNational529Only,
  hasOwnedManaged
} from 'state/account/AccountSelectors';

const PERFORMANCE_PATH = '/performance';
const copyRight = `Copyright ${moment.utc().year()}`;

const SocialMediaLinks = () => (
    <div className="social-media-links">
        <label>Connect with us</label>
        <a
            rel="noreferrer"
            target="_blank"
            className="facebook"
            href="https://www.facebook.com/troweprice"
            style={{ backgroundImage: `url(${socialIcons})` }}>
            Facebook    
        </a>

        <a
            rel="noreferrer"
            target="_blank"
            className="twitter"
            href="https://twitter.com/troweprice"
            style={{ backgroundImage: `url(${socialIcons})` }}>
            Twitter
        </a>

        <a
            target="_blank"
            className="youtube"
            href="https://www.youtube.com/trowepricegroup"
            style={{ backgroundImage: `url(${socialIcons})` }}
            rel="noreferrer">
            YouTube
        </a>

        <a
            rel="noreferrer"
            target="_blank"
            className="linkedin"
            href="https://www.linkedin.com/company/157329?trk=tyah"
            style={{ backgroundImage: `url(${socialIcons})` }}>
            LinkedIn
        </a>
    </div>
);

export const CompanyLinks = ({ view }) => {
    const { hasMaryland529, hasAlaska529, hasNational529 } = view;

    return (
        <div className="company-links">
            <a href={footerLinks.companyOverviewUrl}>Company Overview</a>
            <a href={footerLinks.socialResponsibilityUrl}>Responsibility</a>
            <a href={footerLinks.careersUrl}>Careers</a>
            <a href={footerLinks.investorRelationsUrl}>Investor Relations</a>
            <a href={footerLinks.pressReleasesUrl}>Press Releases</a>
            {hasMaryland529 && (
                <a
                    href={footerLinks.maryland529.privacyPolicyUrl}
                    target="_blank"
                    rel="noreferrer">
                    MD529 Privacy Policy
                </a>
            )}
            {hasAlaska529 && (
                <a
                    href={footerLinks.alaska529.privacyPolicyUrl}
                    target="_blank"
                    rel="noreferrer">
                    Alaska 529 Privacy Policy
                </a>
            )}
            {hasNational529 && (
                <a
                    href={footerLinks.national529.privacyPolicyUrl}
                    target="_blank"
                    rel="noreferrer">
                    T. Rowe Price College Savings Plan Privacy Policy
                </a>
            )}
        </div>
    );
};

const SiteLinks = () => (
    <div>
        <a href={footerLinks.siteMapUrl}>Site Map</a>
        <a href={footerLinks.privacyPolicyUrl}>U.S. &amp; Canada Privacy Notice</a>
        <a href={footerLinks.termsOfUseUrl}>Terms of Use</a>
        <a href={footerLinks.securityMeasuresUrl}>Security Measures</a>
        <a href={footerLinks.legalInformationUrl}>Legal Information</a>
        <a href={footerLinks.customerAgreementUrl}>Customer Agreement</a>
    </div>
);

const AdditionalLinks = ({ view }) => (
    <div>
        <a href={footerLinks.mobileSolutionsUrl}>Mobile Solutions</a>
        <a
            href={
                view.hasNational529Only
                    ? footerLinks.national529.contactUsUrl
                    : footerLinks.contactUsUrl
            }>
            Contact Us
        </a>
    </div>
);

const Has529OnlyLinks = ({ view }) => (
    <div className="college-savings-only-links">
        <Optioner val={view}>
            {{
                hasAlaska529Only: (
                    <a
                        href={footerLinks.alaska529.privacyPolicyUrl}
                        target="_blank"
                        rel="noreferrer">
                        Alaska 529 Privacy Policy
                    </a>
                ),
                hasMaryland529Only: (
                    <a
                        href={footerLinks.maryland529.privacyPolicyUrl}
                        target="_blank"
                        rel="noreferrer">
                        MD529 Privacy Policy
                    </a>
                ),
                hasNational529Only: (
                    <a
                        href={footerLinks.national529.privacyPolicyUrl}
                        target="_blank"
                        rel="noreferrer">
                        T. Rowe Price College Savings Plan Privacy Policy
                    </a>
                ),
                _: null
            }}
        </Optioner>
        <Optioner val={view}>
            {{
                hasAlaska529Only: (
                    <a
                        href={footerLinks.alaska529.contactUsUrl}
                        target="_blank"
                        rel="noreferrer">
                        Contact Us
                    </a>
                ),
                hasMaryland529Only: (
                    <a
                        href={footerLinks.maryland529.contactUsUrl}
                        target="_blank"
                        rel="noreferrer">
                        Contact Us
                    </a>
                ),
                hasNational529Only: (
                    <a
                        href={footerLinks.national529.contactUsUrl}
                        target="_blank"
                        rel="noreferrer">
                        Contact Us
                    </a>
                ),
                _: null
            }}
        </Optioner>
    </div>
);

const DefaultCopyright = ({ hasCollegeSavingsOnly }) => (
    <p className="no-brokerage-copyright">
        {copyRight}, <TRP /> Investment Services, Inc., Distributor
        {hasCollegeSavingsOnly && '/Underwriter'}. All rights reserved. <TRP />,
        Invest with Confidence, and the bighorn sheep design are, collectively
        and/or apart, trademarks of <TRP /> Group, Inc. All other trademarks are the
        property of their respective owners.
    </p>
);

const Maryland529Copyright = () => (
    <p className="no-brokerage-copyright">
        {copyRight}, T. Rowe Price Investment Services, Inc., Distributor and Agent.
        Units in Trust are serviced by T. Rowe Price Services, Inc. All rights
        reserved.
    </p>
);

const BrokerageCopyright = () => (
    <div>
        <p className="brokerage-info">
            {copyRight}, Brokerage and TradeLink accounts are offered by <TRP />{' '}
            Investment Services, Inc., member FINRA/SIPC, and are carried by Pershing
            LLC, a BNY Mellon Company, member NYSE/FINRA/SIPC, which acts as a
            clearing broker for <TRP /> Investment Services, Inc.
            <TRP /> Investment Services, Inc., does not provide investment advice or
            recommendations.
        </p>
        <p className="brokerage-copyright">
            <a className="prospectus-link" href={footerLinks.copyrightUrl}>
                {copyRight}, Pershing LLC. All Rights Reserved. Click here for terms of
                use.
            </a>
        </p>
    </div>
);

const ActivePlusCopyright = () => (
    <div>
        <p className="active-plus-portfolios-disclaimer">
            The <TRP /> ActivePlus Portfolios
            <i className="reg" /> program is a discretionary managed account program
            and <TRP /> Retirement Advisory Service <i className="tm" /> is a
            non-discretionary financial planning service and a discretionary managed
            account program provided by <TRP /> Advisory Services, Inc., a registered
            investment adviser under the Investment Advisers Act of 1940. Brokerage
            accounts for these programs are provided by <TRP /> Investment Services,
            Inc., member FINRA/SIPC, and are carried by Pershing LLC, a BNY Mellon
            Company, member NYSE/FINRA/SIPC, which acts as a clearing broker for{' '}
            <TRP /> Investment Services, Inc. <TRP /> Advisory Services, Inc., and{' '}
            <TRP /> Investment Services, Inc., are affiliated companies.
        </p>
        <p className="active-plus-portfolios-copyright">{copyRight}</p>
    </div>
);

const BrokerageAndActivePlusCopyright = ({ hasManaged }) => (
    <div>
        <p className="brokerage-info with-app">
            Brokerage accounts are offered by <TRP /> Investment Services, Inc.,
            member FINRA/SIPC, and are carried by Pershing LLC, a BNY Mellon Company,
            member NYSE/FINRA/SIPC, which acts as a clearing broker for <TRP />{' '}
            Investment Services, Inc.
            <TRP /> Investment Services, Inc., does not provide investment advice or
            recommendations.
        </p>
        <p className="active-plus-portfolios-disclaimer">
            The <TRP /> ActivePlus Portfolios
            <i className="reg" /> program is a discretionary managed account program
            and <TRP /> Retirement Advisory Service <i className="tm" /> is a
            non-discretionary financial planning service and a discretionary managed
            account program provided by <TRP /> Advisory Services, Inc., a registered
            investment adviser under the Investment Advisers Act of 1940. Brokerage
            accounts for these programs are provided by <TRP /> Investment Services,
            Inc., member FINRA/SIPC, and are carried by Pershing LLC, a BNY Mellon
            Company, member NYSE/FINRA/SIPC, which acts as a clearing broker for{' '}
            <TRP /> Investment Services, Inc. <TRP /> Advisory Services, Inc., and{' '}
            <TRP /> Investment Services, Inc., are affiliated companies.
        </p>
        <p className="active-plus-portfolios-copyright">
            {hasManaged && copyRight}
        </p>
    </div>
);

const ThisWebsiteDisclaimer = () => (
    <p>
        This website does not provide investment advice or recommendations. Nothing
        in this website shall be considered a solicitation to buy or an offer to
        sell a security, or any other product or service, to any person in any
        jurisdiction where such offer, solicitation, purchase, or sale would be
        unlawful under the laws of such jurisdiction.
    </p>
);

const CollegeSavingsPlanOnlyCopyright = () => (
    <div className="college-savings-plan-only-copyright">
        <p>
            {copyRight}, <TRP /> Investment Services, Inc., Distributor and Agent.
            Units in Trust are serviced by <TRP /> Services, Inc. All rights reserved.
        </p>
    </div>
);

const DefaultDisclaimer = ({ hasManaged }) => (
    <div>
        <p>
      <span className="mutual-fund-without-529">
        The mutual funds referred to in this website are offered and sold only
        to persons residing in the United States and are offered by prospectus
        only. The prospectuses include investment objectives, risks, fees,
        expenses, and other information that you should read and consider
        carefully before investing.
      </span>
            <a className="prospectus-link" href={footerLinks.prospectusUrl}>
                Download a prospectus
            </a>
        </p>
        {!hasManaged && <ThisWebsiteDisclaimer />}
    </div>
);

const MissionStatement = () => (
    <div>
        <label>Our Mission Is Simple.</label>
        <p>
            Help clients around the world achieve their long-term investment goals.
        </p>
    </div>
);

export const BrokerageCopy = () => (
    <div className="footer-brokerage-copy-container">
        <p className="footer-brokerage-copy">
            Quote data delayed per exchange agreement - Data delayed at least 15
            minutes for NYSE, AMEX, NASDAQ, OTC, OTCBB and OPRA.
            <br />
            <br />
            Delayed pricing of holdings is for domestic equities and options only.
            Pricing of other positions is as of the previous day&apos;s close, or the
            most recently available price. Positions are priced as of the last trade.
            The current bid or ask may be different from the price displayed.
            <br />
            <br />
            Intraday Holdings do not account for orders that are executed but have not
            yet been reported by the exchanges/market makers. Please check your orders
            before placing trades.
        </p>
    </div>
);

const Footer = ({
                    isMobile,
                    view,
                    onAccountsPage,
                    isDashboardOrAccounts,
                    hasReceivedAccounts,
                    hasOwnedMaryland529OnlyFlag,
                    hasOwnedNational529OnlyFlag,
                    hasOwnedAlaska529OnlyFlag,
                    hasOwnedMaryland529Flag,
                    hasOwnedNational529Flag,
                    hasOwnedAlaska529Flag,
                    hasOwnedCollegeSavingsOnlyFlag,
                    hasOwnedManagedFlag,
                    hasOwnedBrokerageFlag
                }) => {
    // TODO: Refactor this block once develop-2019 gets merged down to develop. Refactoring
    // because develop-2019 will include code that has access to router state
    const { location: { pathname } = {} } = window || {};

    const {
        hasManaged,
        hasAlaska529Only,
        hasBrokerage,
        hasCollegeSavingsOnly,
        hasMaryland529Only,
        hasNational529Only
    } = view;

    const has529Only = owned529OnlyBrandingEnabled()
      ? hasOwnedAlaska529OnlyFlag || hasOwnedMaryland529OnlyFlag || hasOwnedNational529OnlyFlag
      : hasAlaska529Only || hasMaryland529Only || hasNational529Only;

    const includesBrokerageAndOthers = (product) => (owned529OnlyBrandingEnabled()
      ? hasOwnedBrokerageFlag
      : hasBrokerage) && product;
    const isLoading = isDashboardOrAccounts && !hasReceivedAccounts;

    const owned529View = {
      ...view,
      hasAlaska529: hasOwnedAlaska529Flag,
      hasMaryland529: hasOwnedMaryland529Flag,
      hasNational529: hasOwnedNational529Flag,
      hasAlaska529Only: hasOwnedAlaska529OnlyFlag,
      hasMaryland529Only: hasOwnedMaryland529OnlyFlag,
      hasNational529Only: hasOwnedNational529OnlyFlag
    }

    const disclaimerClassnames = classNames({
        disclaimer: true,
        'college-savings-only': has529Only
    });

    return isLoading ? (
        <FooterLoadingSkeleton />
    ) : isMobile ? null : (
        <section className="footer" data-metrics-location="FTR">
            {onAccountsPage && !has529Only && (
                <div className="factset-container">
                    <Factset />
                </div>
            )}
            {isDashboardOrAccounts && (owned529OnlyBrandingEnabled()
              ? hasOwnedBrokerageFlag
              : hasBrokerage) && intradayRefreshEnabled() && (
                <BrokerageCopy />
            )}
            <div className="site-map">
                {!has529Only && (
                    <div className="wrap">
                        <p
                            className="tagline"
                            style={{ backgroundImage: `url(${headingImage})` }}
                        />
                        <MissionStatement />
                        <SocialMediaLinks />
                        <CompanyLinks view={owned529OnlyBrandingEnabled()
                          ? owned529View
                          : view} />
                        <SiteLinks view={owned529OnlyBrandingEnabled()
                  ? owned529View
                  : view} />
                        <AdditionalLinks view={owned529OnlyBrandingEnabled()
                  ? owned529View
                  : view} />
                    </div>
                )}
                {has529Only && <Has529OnlyLinks view={owned529OnlyBrandingEnabled()
                  ? owned529View
                  : view} />}
            </div>

            {pathname.includes(PERFORMANCE_PATH) && <FooterDisclosures />}

            {!has529Only && (
                <div className="disclaimer">
                    <div className="wrap">
                        <DefaultDisclaimer hasManaged={(owned529OnlyBrandingEnabled()
                          ? hasOwnedManagedFlag
                          : hasManaged)} />
                        {includesBrokerageAndOthers((owned529OnlyBrandingEnabled()
                          ? hasOwnedManagedFlag
                          : hasManaged)) || (owned529OnlyBrandingEnabled()
                          ? hasOwnedManagedFlag
                          : hasManaged) ? (
                            <BrokerageAndActivePlusCopyright hasManaged={(owned529OnlyBrandingEnabled()
                              ? hasOwnedManagedFlag
                              : hasManaged)} />
                        ) : (owned529OnlyBrandingEnabled()
                          ? hasOwnedBrokerageFlag
                          : hasBrokerage) ? (
                            <BrokerageCopyright />
                        ) : (
                            <div>
                                <DefaultCopyright
                                    hasCollegeSavingsOnly={owned529OnlyBrandingEnabled()
                                  ? hasOwnedCollegeSavingsOnlyFlag
                                  : hasCollegeSavingsOnly
                                }
                                />
                                {(owned529OnlyBrandingEnabled()
                                  ? hasOwnedManagedFlag
                                  : hasManaged) ? <ActivePlusCopyright /> : null}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {has529Only && (
                <div className={disclaimerClassnames}>
                    <div className="wrap">
                        {(owned529OnlyBrandingEnabled()
                          ? hasOwnedMaryland529OnlyFlag
                          : hasMaryland529Only) ? (
                            <Maryland529Copyright />
                        ) : (
                            <CollegeSavingsPlanOnlyCopyright
                                hasNational529Only={owned529OnlyBrandingEnabled()
                                  ? hasOwnedNational529OnlyFlag
                                  : hasNational529Only
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

function mergeProps(stateProps, dispatchProps, ownProps) {
    return Object.assign({}, ownProps, {
        ...ownProps,
        ...stateProps,
        ...dispatchProps,
        view: ownProps.view ? ownProps.view : stateProps.view
    });
}

const mapStateToProps = (state) => {
    return {
        isMobile: isMobile(state),
        view: getView(state),
        onAccountsPage: onAccountsPage(state),
        isDashboardOrAccounts: isDashboardOrAccounts(state),
        hasReceivedAccounts: getHasReceivedAccounts(state),
        hasOwnedMaryland529OnlyFlag: hasOwnedMaryland529Only(state),
        hasOwnedNational529OnlyFlag: hasOwnedNational529Only(state),
        hasOwnedAlaska529OnlyFlag: hasOwnedAlaska529Only(state),
        hasOwnedMaryland529Flag: hasOwnedMaryland529(state),
        hasOwnedNational529Flag: hasOwnedNational529(state),
        hasOwnedAlaska529Flag: hasOwnedAlaska529(state),
        hasOwnedCollegeSavingsOnlyFlag: getHasOwnedCollegeSavingsOnly(state),
        hasOwnedManagedFlag: hasOwnedManaged(state),
        hasOwnedBrokerageFlag: hasOwnedBrokerage(state)
    };
};

export default withRouter(connect(mapStateToProps, null, mergeProps)(Footer));